import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import {
  getDefaultOrganizationTestingEndDate,
  OrganizationItem,
  OrganizationItemStore,
  ORGANIZATIONS_MEMBER_LIMIT,
} from 'src/stores/organizations-store';
import { container } from 'src/inversify.config';
import { Form, Alert, Switch, Input, Spin, Row, Tabs, Col, Button, Space, Checkbox, DatePicker, App, Divider } from 'antd';
import { isEmailOrGuid, nameof } from 'src/core/utils/object';
import LanguageSelect from 'src/common/components/business/language-select';
import UserAutocomplete from 'src/common/components/business/user-autocomplete';
import DefaultRolesSelect from 'src/common/components/business/default-roles-select';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Country, Selector } from 'src/common/types';
import { useIdentity } from 'src/core/services/authentication';
import NewItemModal from 'src/core/ui/newitemmodal';
import { LicenseManagerRoleName, StructmasterUserRoleName } from '../../stores/roles-store';
import LicenseAttributesComponent from '../licenses/license-attributes/license-attributes';
import { LicenseCreationAttributesStore, LicenseManagerAssignmentAttributes, NewLicensCreationAttributes } from '../../stores/licensing-store';
import { UserSummaryStore } from '../../stores/users-store';
import { Query } from '../../core/stores/data-store';
import SelectCountry from 'src/common/components/business/country-select';
import moment from 'moment';
import { getDateFormat } from 'src/common/utils';
import { Typography } from 'antd';
import { RoleAssignmentAttributes } from 'src/stores/role-assignments-store';
import RoleAssignmentAttributesComponent from '../role-assignments/role-attributes/role-attributes';
import { RuleObject } from 'antd/es/form';
import { curateString } from 'src/core/utils/object';

const { Text } = Typography;

interface FormData {
  displayName: string;
  email: string;
  address: string;
  defaultLanguage: string;
  isPartner: boolean;
  ownerUser: { label: string; value: string };
  country: Country;
  initialUsers: { user: { label: string; value: string }; roles: { label: string; value: string }[] }[];
  isTestingOrg: boolean;
  testingEndDate?: Date;
  ownerRolesExpirationDate: Date;
}

const NewOrganizationDialog: FC<{
  canCreateOrg: boolean;
  canCreatePartnerOrg: boolean;
  onClose: (response?: CommandResult<OrganizationItem>, isPartner?: boolean) => void;
}> = ({ onClose, canCreateOrg, canCreatePartnerOrg }) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const [form] = Form.useForm<FormData>();

  const { TextArea } = Input;

  const newOrganizationStore = container.get(OrganizationItemStore);
  const newOrganizationState = newOrganizationStore.state;
  const [savedAndError, setSavedAndError] = useState(false);
  const [isPartnerCheck, setIsPartnerCheck] = useState(false);
  const [customError, setCustomError] = useState('');
  const licenseCreationAttributesStore = container.get(LicenseCreationAttributesStore);
  const [licenseAttributesVisible, setLicenseAttributesVisible] = useState<{ [key: number]: boolean }>({});
  const [licenseAttributes, setLicenseAttributes] = useState<{ [key: number]: LicenseManagerAssignmentAttributes }>();
  const [roleAttributes, setRoleAttributes] = useState<{ [key: number]: RoleAssignmentAttributes }>();

  const usersStore = useMemo(() => container.get(UserSummaryStore), []);
  const usersState = usersStore.state;
  const [isTestOrg, setIsTestOrg] = useState(false);
  const { modal } = App.useApp();

  const currentOrganizationMaster = useRef('');
  const onNewItem = async (item: any) => {
    item.isPartner = isPartnerCheck;
    let membersWithRoles: any[] = [];
    const users = (item as any).initialUsers as { user: { value: string; label: string }; roles: { value: string; label: string }[] }[];
    (item as any).users = undefined;
    for (let i = 0; i < (users || []).length; i++) {
      let user = users[i];
      membersWithRoles.push({
        userIdOrEmail: user.user.value,
        defaultRoleIds: (user.roles || []).map((r) =>
          roleAttributes && roleAttributes[i].expirationDate
            ? { role: r.label, roleId: r.value, expirationDate: roleAttributes[i].expirationDate }
            : { role: r.label, roleId: r.value, expirationDate: undefined }
        ),
      });
    }
    let response = await newOrganizationStore.create({
      address: curateString(item.address),
      defaultLanguage: item.defaultLanguage,
      displayName: curateString(item.displayName),
      email: item.email,
      isPartner: item.isPartner,
      ownerUserIdOrEmail: item.ownerUser.value,
      initialUsers: membersWithRoles,
      country: item.country,
      isTestingOrg: item.isTestingOrg,
      testingEndDate: item.testingEndDate,
      ownerRolesExpirationDate: item.ownerRolesExpirationDate,
    });

    if (response && response?.isSuccess && response.identity) {
      var query = {
        take: 10000,
        filter: [{ Organizations: { any: { OrganizationId: { value: response.identity, type: 'guid' } } } }],
      } as Query;
      await usersStore.load(query);
      createLicenseAttributes(membersWithRoles, response.identity).then(() => {
        form.resetFields();
        onClose(response, item.isPartner);
        setSavedAndError(false);
      });
    } else {
      setSavedAndError(true);
    }
  };
  function onChange(checked: boolean) {
    setIsPartnerCheck(checked);
  }

  useEffect(() => {
    setSavedAndError(false);
    newOrganizationStore.clearMessages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canCreateOrg && !canCreatePartnerOrg) return null;
  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes, fieldIndx: number) => {
    var attr = { ...licenseAttributes };
    attr[fieldIndx] = item;
    setLicenseAttributes(attr);
  };
  const onRoleAttributesChange = (item: RoleAssignmentAttributes, fieldIdx: number) => {
    var attr = { ...roleAttributes };
    attr[fieldIdx] = item;
    setRoleAttributes(attr);
  };
  const getUserId = (userIdOrEmail: string) => {
    let user = usersState.items.value.find((x) => userIdOrEmail === x.id || userIdOrEmail === x.email);
    if (user) {
      return user.id;
    }
    return undefined;
  };
  const createLicenseAttributes = async (users: any[], organizationId: string) => {
    if (licenseAttributes) {
      var items: NewLicensCreationAttributes[] = Object.entries(licenseAttributes).map((x) => {
        return {
          maximumLicenseDueDate: x[1].maximumLicenseDueDate,
          maxLicensesPeriodInDays: x[1].maxLicensesPeriodInDays,
          maxNumberOfLicensesPerMonth: x[1].maxNumberOfLicensesPerMonth,
          allowedDevices: x[1].allowedDevices,
          identityId: getUserId(users[+x[0]].userIdOrEmail),
          scopeId: organizationId,
          canCreateUnlimitedLicense: x[1].canCreateUnlimitedLicense,
          needsSignOff: x[1].needsSignOff,
          canCreateRecurrentLicenses: x[1].canCreateRecurrentLicenses ?? false,
        } as NewLicensCreationAttributes;
      });

      await licenseCreationAttributesStore.createMany(items.filter((x) => x.identityId !== undefined));
    }
  };
  const onRolesChange = (values: { value: string; label: string; selector: Selector }[], index: number | undefined) => {
    if (index !== undefined) {
      var licenseMnagerRoleSelected = values.any((x) => x.label === LicenseManagerRoleName);
      if (!licenseAttributesVisible[index] && licenseMnagerRoleSelected) {
        let attributesVisible = { ...licenseAttributesVisible };
        attributesVisible[index] = true;
        setLicenseAttributesVisible(attributesVisible);
      } else if (!licenseMnagerRoleSelected && licenseAttributesVisible[index]) {
        let attributesVisible = { ...licenseAttributesVisible };
        attributesVisible[index] = false;
        setLicenseAttributesVisible(attributesVisible);
      }
    }
  };
  return (
    <NewItemModal
      className="figma-modal"
      title={t('AddNewOrganization')}
      onNewItem={async () => {
        let item: any;
        try {
          item = await form.validateFields();
        } catch {
          setCustomError(t('CheckOrganizationInfoAndUsersTabs'));
          return;
        }
        setCustomError('');
        if (isTestOrg) {
          modal.confirm({
            title: t('TestOrgConfirmationMessage'),
            onOk: () => onNewItem(item),
          });
        } else onNewItem(item);
      }}
      onClose={() => onClose(savedAndError ? ({} as any) : undefined)}
      onOkButtonIsDisabled={newOrganizationState.isBusy.get()}
      onCancelButtonIsDisabled={newOrganizationState.isBusy.get()}
      okText={t('AddOrganization')}
      width={720}
      errorMessage={newOrganizationState.errorMessage}
      closable={!newOrganizationState.isBusy.get()}
      maskClosable={!newOrganizationState.isBusy.get()}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin spinning={newOrganizationState.isBusy.get()} size="large">
            <Form form={form} layout="vertical" autoComplete="off" initialValues={{ isPartner: false }}>
              <Tabs defaultActiveKey="1">
                <Tabs.TabPane className="ant-tabs-tabpane-without-border" tab={t('OrganizationInfo')} key="1">
                  <Form.Item
                    name={nameof<FormData>('displayName')}
                    rules={[
                      { required: true, message: t('PleaseEnterName') },
                      () => ({
                        validator: (ruleObj: RuleObject, value: string) => {
                          const maxLenght = 128;
                          if (value === undefined) return Promise.resolve();
                          if (value.length > maxLenght) {
                            return Promise.reject(t('TextFieldLimitError').replace('{0}', t('DisplayName')).replace('{1}', `${maxLenght}`));
                          } else return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input autoComplete="off" allowClear placeholder={t('DisplayName')} />
                  </Form.Item>
                  <Form.Item
                    name={nameof<FormData>('email')}
                    rules={[
                      { required: true, type: 'email', message: t('PleaseEnterAValidEmail') },
                      () => ({
                        validator: (ruleObj: RuleObject, value: string) => {
                          const maxLenght = 256;
                          if (value === undefined) return Promise.resolve();
                          if (value.length > maxLenght) {
                            return Promise.reject(t('TextFieldLimitError').replace('{0}', t('Email')).replace('{1}', `${maxLenght}`));
                          } else return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input autoComplete="off" allowClear placeholder={t('Email')} />
                  </Form.Item>
                  <Form.Item name={nameof<FormData>('address')} rules={[{ required: true, message: t('PleaseEnterAnAddress') }]}>
                    <TextArea rows={3} autoComplete="off" allowClear placeholder={t('Address')} />
                  </Form.Item>
                  <Form.Item name={nameof<FormData>('country')} rules={[{ required: true, message: t('PleaseSelectCountry') }]}>
                    <SelectCountry placeholder={t('Country')} />
                  </Form.Item>
                  <Form.Item
                    name="defaultLanguage"
                    initialValue={identity.profile['default_lang']}
                    rules={[{ required: true, message: t('PleaseSelectALanguage') }]}
                  >
                    <LanguageSelect />
                  </Form.Item>
                  <Row gutter={8} style={{ width: '100%' }}>
                    <Col xxl={16} xl={16} sm={16} lg={16} xs={24}>
                      <Form.Item
                        name={nameof<FormData>('ownerUser')}
                        rules={[
                          () => ({
                            required: true,
                            validator(_, value) {
                              currentOrganizationMaster.current = value?.value;
                              if (value?.value == null) return Promise.reject(t('SelectOrInviteWarning'));
                              if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <UserAutocomplete placeholder={t('SelectOrInviteOrganizationMaster')} onChange={(v) => form.setFieldValue('ownerUser', v)} />
                      </Form.Item>
                    </Col>
                    <Col xxl={8} xl={8} sm={8} lg={8} xs={24}>
                      <RoleAssignmentAttributesComponent
                        customFormFieldName={'ownerRolesExpirationDate'}
                        onChange={() => {}}
                        showLabel={false}
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>

                  <Row gutter={0}>
                    <Col sm={12} xs={24}>
                      <Form.Item name={nameof<FormData>('isTestingOrg')} valuePropName="checked" initialValue={false}>
                        <Checkbox
                          onChange={(e) => {
                            setIsTestOrg(e.target.checked);
                            if (!e.target.checked) {
                              form.setFieldValue('testingEndDate', undefined);
                            }
                          }}
                        >
                          {t('IsTestOrganization')}
                        </Checkbox>
                      </Form.Item>
                    </Col>
                    {isTestOrg && (
                      <>
                        <Col sm={12} xs={24}>
                          <Form.Item
                            initialValue={getDefaultOrganizationTestingEndDate()}
                            name={nameof<FormData>('testingEndDate')}
                            rules={[() => ({ required: true })]}
                          >
                            <div style={{ width: '100' }}>
                              <Row>
                                <Col sm={12} xs={24} style={{ marginTop: 10, paddingRight: 5 }}>
                                  <span style={{ float: 'inline-end' }}>{t('TestingEndDate')}</span>
                                </Col>
                                <Col sm={12} xs={24}>
                                  <Form.Item noStyle name={nameof<FormData>('testingEndDate')}>
                                    <DatePicker
                                      disabledDate={(current) => {
                                        let customDate = moment();
                                        return current && current < moment(customDate);
                                      }}
                                      allowClear={false}
                                      style={{ width: '100%' }}
                                      format={getDateFormat()}
                                    />
                                  </Form.Item>
                                </Col>
                              </Row>
                            </div>
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </Row>
                  {canCreatePartnerOrg && (
                    <Form.Item name={nameof<FormData>('isPartner')} valuePropName="checked">
                      <Row>
                        <Switch onChange={onChange} />
                        <strong style={{ marginLeft: 10 }}>{t('IsPartnerQuestion')}</strong>
                      </Row>
                    </Form.Item>
                  )}
                </Tabs.TabPane>
                <Tabs.TabPane className="ant-tabs-tabpane-without-border" tab={t('OrganizationUsers')} key="2">
                  <Alert type="warning" message={t('OrganizationUserLimitWarningCreation').replace('{0}', `${ORGANIZATIONS_MEMBER_LIMIT}`)} />
                  <Form.List name={nameof<FormData>('initialUsers')}>
                    {(fields, { add, remove }) => (
                      <>
                        <div id="new-organization-users-container" style={{ maxHeight: '40vh', overflowX: 'auto' }}>
                          {fields.map(({ key, name: fieldIndx, fieldKey, ...restField }) => (
                            <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                              <Row key={fieldKey}>
                                <Col xl={11} xs={22}>
                                  <Form.Item
                                    {...restField}
                                    style={{ marginRight: 10 }}
                                    name={[fieldIndx, 'user']}
                                    //fieldKey={[fieldKey, 'user']}
                                    rules={[
                                      () => ({
                                        required: true,
                                        validator(_, value) {
                                          if (value?.value === currentOrganizationMaster.current)
                                            return Promise.reject(t('ThisUserIsOrganizationMaster'));
                                          if (value?.value == null) return Promise.reject(t('SelectOrInviteWarning'));
                                          if (!isEmailOrGuid(value.value)) return Promise.reject(t('InvalidEmailWarning'));
                                          return Promise.resolve();
                                        },
                                      }),
                                    ]}
                                  >
                                    <UserAutocomplete />
                                  </Form.Item>
                                </Col>
                                <Col xl={11} xs={22}>
                                  <Form.Item {...restField} style={{ marginRight: 10 }} name={[fieldIndx, 'roles']}>
                                    <DefaultRolesSelect
                                      excludeNames={[StructmasterUserRoleName]}
                                      onChange={onRolesChange}
                                      placeholder={t('SelectRoles')}
                                      maxSelector={isPartnerCheck ? Selector.Partners : Selector.Organization}
                                      partnersOnly={isPartnerCheck ? undefined : false}
                                      index={fieldIndx}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={2} style={{ textAlign: 'center' }}>
                                  <MinusCircleOutlined style={{ fontSize: 20 }} onClick={() => remove(fieldIndx)} />
                                </Col>
                                <Col span={24}>
                                  <Text style={{ marginBottom: 10 }} strong>
                                    {t('RoleAssignmentAttributes')}:
                                  </Text>
                                  <RoleAssignmentAttributesComponent
                                    customFormFieldName={[fieldIndx, 'roles-expiration']}
                                    onChange={(item) => onRoleAttributesChange(item, fieldIndx)}
                                  />
                                  {licenseAttributesVisible[fieldIndx] && (
                                    <>
                                      <Divider />
                                      <Text style={{ marginBottom: 10 }} strong>
                                        {t('LicenseManagerRoleAttributes')}:
                                      </Text>
                                      <LicenseAttributesComponent
                                        onChange={(item: LicenseManagerAssignmentAttributes) => onLicenseAttributesChange(item, fieldIndx)}
                                      />
                                    </>
                                  )}
                                </Col>
                              </Row>
                            </Space>
                          ))}
                        </div>
                        <Form.Item>
                          <Button
                            type="dashed"
                            disabled={fields.length >= ORGANIZATIONS_MEMBER_LIMIT}
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                          >
                            {t('AddUser')}
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Tabs.TabPane>
              </Tabs>
            </Form>
          </Spin>
        </>
      }
    />
  );
};

export default NewOrganizationDialog;
